import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useReportTypes} from '../hooks/useReportTypes';
import {useReportTypesFilter} from '../hooks/useReportTypesFilter';
import {useReportTypesSearch} from '../hooks/useReportTypesSearch';
import {ReportType} from '../types';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody) to the shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows — but it structurally
// could not have sorting or the loading/empty/error states, because those come
// from the shared component (§3z).
//
// ⚠️ NO TOKEN PAGINATION, and it is NOT missing (§9). `listReportTypes` takes
// no arguments and returns no `nextToken` — the one response IS the whole
// catalogue, roughly 30 rows defined by Stripe. There is no second page for a
// DataTableTokenPagination footer to offer, so adding one would render a
// "load more" control that can never load anything. See useReportTypesQuery
// for the full reasoning. This is the same judgement as the missing selection
// checkboxes below: an affordance with nothing behind it is worse than no
// affordance.
//
// ⚠️ NO ROW SELECTION and no batch actions, also deliberate. Report types are
// Stripe's catalogue: the API exposes list/get only, with no create, update or
// delete for this app to call. A checkbox column would drive nothing.
//
// This module is READ-ONLY. That is stated here, in useTools, and in
// useReportTypesQuery, because "read-only by design" and "the mutations were
// forgotten" look identical in a diff.

export const RecordsTable = () => {

    const navigate = useNavigate();

    const {filters} = useReportTypesFilter();

    const {
        columns,
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
    } = useReportTypes(filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useReportTypesSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both. The no-data case here would be
    // genuinely surprising — Stripe always has a catalogue — so it reads as
    // the API problem it would be, rather than as an ordinary empty list.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No report types match this search')
        : I18n.get('No report types available');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown; the previous table passed `limit={10}` and a no-op
              * handler to satisfy it, so the dropdown rendered and did nothing.
              * It was doubly wrong here — there is only ever one page (§9).
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load report types')}
                >
                    {I18n.get(
                        'The billing API did not return the report type catalogue. '
                        + 'Try Reload; if it persists the API may be unavailable in '
                        + 'this environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<ReportType>
                columns={columns}
                records={visibleItems}
                // Sorting covers the WHOLE catalogue here, not a loaded window
                // — there is only one page.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}: {record: ReportType}) =>
                    navigate(`/report-types/${encodeURIComponent(record.id)}`)}
            />

            {/*
              * A plain count, not DataTableTokenPagination: with no nextToken
              * there is no "have I seen everything?" question to answer — the
              * answer is always yes. Stating the number is still worth doing
              * when a filter is active, so the reader knows the catalogue was
              * narrowed rather than short.
              *
              * Pluralised, because "1 report types" is wrong and one match is
              * exactly when someone reads this carefully (§9).
              */}
            {!loading && !isError && items.length > 0 && (
                <div>
                    {keyword.trim() !== '' || visibleItems.length !== items.length
                        ? I18n.get(
                            `${visibleItems.length} of ${items.length} report `
                            + `type${items.length === 1 ? '' : 's'} shown`)
                        : I18n.get(
                            `${items.length} report `
                            + `type${items.length === 1 ? '' : 's'}`)}
                </div>
            )}
        </SimpleGrid>
    );
};
